/* eslint-disable @typescript-eslint/no-explicit-any */
"use client";

import * as React from "react";
import Image from "next/image";
import { Swiper, SwiperSlide } from "swiper/react";
import { Navigation, Pagination, Autoplay } from "swiper/modules";
import { ChevronLeft, ChevronRight } from "lucide-react";
import { cn } from "@/lib/utils";
import { fotoDeFicha, siguienteIntento } from "@/lib/foto-de-ficha";

// Import Swiper styles
import "swiper/css";
import "swiper/css/navigation";
import "swiper/css/pagination";

// Una tarjeta ocupa toda la pantalla en el móvil (~400 px a densidad 2–3) y
// un tercio en escritorio.
const ANCHO_TARJETA = 1000;

interface CarouselProps {
  images: string[];
  slug?: string;
  locale?: string;
  slidesPerView?: number;
  className?: string;
  showNavigation?: boolean;
  showPagination?: boolean;
  autoplay?: boolean;
  autoplayDelay?: number;
}

interface CarouselNormalProps {
  images: string[];
  slug?: string;
  locale?: string;
  slidesPerView?: number;
  className?: string;
}

export const Carousel: React.FC<CarouselProps> = ({
  images,
  slug,
  locale,
  slidesPerView = 1,
  className,
  showNavigation = true,
  showPagination = true,
  autoplay = false,
  autoplayDelay = 3000,
}) => {
  const swiperRef = React.useRef<any>(null);

  const handlePrev = () => {
    if (swiperRef.current) {
      swiperRef.current.slidePrev();
    }
  };

  const handleNext = () => {
    if (swiperRef.current) {
      swiperRef.current.slideNext();
    }
  };

  if (!images || images.length === 0) {
    return (
      <div className={cn("w-full h-48 bg-gray-200 rounded-md flex items-center justify-center", className)}>
        <span className="text-gray-500">No images available</span>
      </div>
    );
  }

  return (
    <div className={cn("relative w-full carousel-white-dots", className)}>
      <Swiper
        modules={[Navigation, Pagination, Autoplay]}
        spaceBetween={0}
        slidesPerView={slidesPerView}
        navigation={false}
        pagination={showPagination ? { clickable: true } : false}
        autoplay={autoplay ? { delay: autoplayDelay, disableOnInteraction: false } : false}
        onSwiper={(swiper) => {
          swiperRef.current = swiper;
        }}
        className="w-full h-full"
      >
        {images.map((image, index) => {
          // Check if image is from external domains that cause 402 errors - use unoptimized to avoid issues
          // These domains may block or limit Next.js Image Optimization requests
          // Using case-insensitive check to catch all variations
          const imageUrl = (image || '').toLowerCase();
          const externalImageDomains = [
            'cdn.witei.com',
            'd2ctzk1imdlpfx.cloudfront.net',
            '.cloudfront.net', // Catch all CloudFront domains (with dot to avoid false positives)
          ];
          const isExternalImage = externalImageDomains.some(domain => imageUrl.includes(domain.toLowerCase()));
          // Las fotos que puede servir Cloudinary llegan ya reducidas y en AVIF/WebP,
          // sin pasar además por la optimización de Vercel.
          const foto = fotoDeFicha(image, ANCHO_TARJETA);
          const porCloudinary = foto !== image;
          
          return (
            <SwiperSlide key={index}>
              <div className="relative w-full aspect-[3/2]">
                <Image
                  src={foto}
                  alt={`Property image ${index + 1}`}
                  fill
                  sizes={isExternalImage 
                    ? "(max-width: 640px) 100vw, (max-width: 1024px) 50vw, (max-width: 1280px) 33vw, 1200px"
                    : "(max-width: 768px) 100vw, (max-width: 1200px) 50vw, 33vw"
                  }
                  className="object-cover"
                  priority={index === 0}
                  loading={index === 0 ? undefined : "lazy"}
                  quality={isExternalImage ? 75 : 85}
                  unoptimized={isExternalImage || porCloudinary}
                  referrerPolicy={isExternalImage ? "no-referrer" : "no-referrer-when-downgrade"}
                  onError={(e) => {
                    const siguiente = siguienteIntento(e.currentTarget.src);
                    if (siguiente) e.currentTarget.src = siguiente;
                  }}
                />
              </div>
            </SwiperSlide>
          );
        })}
      </Swiper>

      {showNavigation && images.length > 1 && (
        <>
          <button
            onClick={handlePrev}
            className="absolute left-2 top-1/2 -translate-y-1/2 z-10 bg-white/80 hover:bg-white rounded-full p-2 shadow-md transition-colors"
            aria-label="Previous image"
          >
            <ChevronLeft className="h-4 w-4" />
          </button>
          <button
            onClick={handleNext}
            className="absolute right-2 top-1/2 -translate-y-1/2 z-10 bg-white/80 hover:bg-white rounded-full p-2 shadow-md transition-colors"
            aria-label="Next image"
          >
            <ChevronRight className="h-4 w-4" />
          </button>
        </>
      )}
    </div>
  );
};

// Las tarjetas (portada, resultados y mapa) sólo enseñan las primeras fotos:
// con todas, los puntos de paginación desbordaban la foto. La galería
// completa está en la ficha.
const MAX_FOTOS_TARJETA = 5;

export const CarouselNormal: React.FC<CarouselNormalProps> = ({
  images,
  slug,
  locale,
  slidesPerView = 1,
  className,
}) => {
  return (
    <Carousel
      images={images?.slice(0, MAX_FOTOS_TARJETA)}
      slug={slug}
      locale={locale}
      slidesPerView={slidesPerView}
      className={className}
      showNavigation={true}
      showPagination={true}
      autoplay={false}
    />
  );
};
